<template>
  <!-- ========== Left Sidebar Start ========== -->

  <!--- Sidemenu -->
  <div id="sidebar-menu">
    <!-- Left Menu Start -->
    <ul id="side-menu" class="metismenu list-unstyled">
      <router-link
        v-if="backRouteLink"
        :to="backRouteLink"
        :class="`go-back-link${sidebarDisplayedState ? '' : '-minimized'}`"
      >
        <i class="bx bx-left-arrow-circle"></i> <span v-if="sidebarDisplayedState">{{ backRouteLabel }}</span>
      </router-link>
      <template v-for="item in filterItems(menuItems)">
        <li class="menu-title" v-if="item.isTitle" :key="item.id">
          <b-badge v-if="item.isBranchMode" :class="`${item.bagdeColor()} me-1`">
            <i class="bx bx-git-branch fw-bold"> </i>
          </b-badge>
          {{ handleComputedLabel(item.label) }}
        </li>
        <li
          v-if="!item.isTitle && !item.isLayout"
          :key="item.id"
          :class="{
            'mm-active': item.opened,
          }"
        >
          <a
            v-if="hasItems(item)"
            href="javascript:void(0);"
            class="is-parent"
            :class="{ 'has-arrow': !item.badge, 'has-dropdown': item.badge }"
            @click="toggleOpened(item)"
          >
            <i
              :class="`bx ${item.icon} ${getActiveColor(item)}`"
              v-if="(item.icon && !item.showIconOnlyWhenCollapse) || (item.icon && !sidebarDisplayedState)"
            ></i>

            <!-- Collapsible for multi level -->
            <span :class="getActiveColor(item)">
              {{
                splitLabel(handleComputedLabel(item.label)).start !== ''
                  ? ` ${splitLabel(handleComputedLabel(item.label)).start}`
                  : ''
              }}
              <span class="text-nowrap">
                {{ splitLabel(handleComputedLabel(item.label)).end }}
                <i v-if="item.accessForbidden || restrictedMenuEntry(item)" class="bx bx-lock-alt icon"></i
              ></span>
            </span>
            <span :class="`badge badge-pill badge-${item.badge.variant} float-end`" v-if="item.badge">
              {{ $t(item.badge.text) }}
            </span>
          </a>
          <!-- SINGLE LEVEL -->
          <a v-if="isFullUrl(item)" :href="item.link.href" target="_blank" class="side-nav-link-ref">
            {{ handleComputedLabel(item.label) }}
          </a>
          <router-link
            :to="{ ...item.link }"
            v-else-if="!hasItems(item)"
            class="side-nav-link-ref"
            :class="{
              disabled: isLinkDisabled(item) || restrictedMenuEntry(item),
              'mm-active': item.active,
            }"
          >
            <i :class="`bx ${item.icon}`" v-if="item.icon"></i>
            <span>
              {{
                splitLabel(handleComputedLabel(item.label)).start !== ''
                  ? ` ${splitLabel(handleComputedLabel(item.label)).start}`
                  : ''
              }}
              <span class="text-nowrap">
                {{ splitLabel(handleComputedLabel(item.label)).end }}
                <i v-if="isLinkDisabled(item) || restrictedMenuEntry(item)" class="bx bx-lock-alt icon"></i>
              </span>
            </span>
            <span :class="`badge badge-pill badge-${item.badge.variant} float-end`" v-if="item.badge">
              {{ $t(item.badge.text) }}
            </span>
          </router-link>
          <!-- MULTI LEVEL -->
          <b-collapse :model-value="item.opened || item.forcedOpened || !sidebarDisplayedState">
            <ul v-if="hasItems(item)" class="sub-menu">
              <li
                v-for="(subitem, subitemIndex) of filterItems(item.subItems)"
                :key="subitemIndex"
                :class="{
                  'mm-active': subitem.opened,
                  'd-flex align-items-center': subitem.badge,
                }"
              >
                <!-- Level 2 -->
                <a v-if="isFullUrl(subitem)" :href="subitem.link.href" target="_blank" class="side-nav-link-ref">
                  {{ handleComputedLabel(subitem.label) }}
                </a>
                <router-link
                  :to="getLink(subitem)"
                  v-else-if="!hasItems(subitem) && !restrictedMenuEntry(subitem)"
                  class="side-nav-link-ref"
                  :data-testid="subitem.dataTestId ? subitem.dataTestId : null"
                  :class="{
                    disabled: isLinkDisabled(subitem) || (isContextualSideNav && subitem.active),
                    'mm-active': subitem.active,
                    'pe-0': subitem.badge,
                  }"
                >
                  {{ splitLabel(handleComputedLabel(subitem.label)).start }}
                  <span class="text-nowrap">
                    {{ splitLabel(handleComputedLabel(subitem.label)).end }}
                    <i v-if="isLinkDisabled(subitem)" class="bx bx-lock-alt icon"></i>
                  </span>
                </router-link>
                <b-badge v-if="subitem.badge" :variant="subitem.badge.variant" pill>
                  {{ subitem.badge.text }}
                </b-badge>
                <!-- Level 3 collapsible -->
                <a
                  v-if="hasItems(subitem) && !restrictedMenuEntry(subitem)"
                  class="side-nav-link-a-ref has-arrow"
                  href="javascript:void(0);"
                  @click="toggleOpened(subitem)"
                >
                  {{ handleComputedLabel(subitem.label) }}
                  <span v-if="subitem.accessForbidden"><i class="bx bx-lock-alt icon"></i></span>
                </a>
                <b-collapse :model-value="subitem.opened || subitem.forcedOpened || !sidebarDisplayedState">
                  <ul v-if="hasItems(subitem)" class="sub-menu" aria-expanded="false">
                    <li
                      v-for="(subSubitem, subSubitemIndex) of filterItems(subitem.subItems)"
                      :key="subSubitemIndex"
                      :class="{
                        'd-flex align-items-center': subSubitem.badge,
                      }"
                    >
                      <!-- Level 3  -->
                      <a
                        v-if="isFullUrl(subSubitem)"
                        :href="subSubitem.link.href"
                        target="_blank"
                        class="side-nav-link-ref"
                      >
                        {{ handleComputedLabel(subSubitem.label) }}
                      </a>
                      <router-link
                        :to="{ ...subSubitem.link }"
                        class="side-nav-link-ref"
                        v-else-if="!hasItems(subSubitem) && !restrictedMenuEntry(subSubitem)"
                        :class="{
                          disabled: isLinkDisabled(subSubitem) || (subSubitem.active && isContextualSideNav),
                          'mm-active': subSubitem.active,
                          'pe-0': subSubitem.badge,
                        }"
                      >
                        {{ splitLabel(handleComputedLabel(subSubitem.label)).start }}
                        <span class="text-nowrap">
                          {{ splitLabel(handleComputedLabel(subSubitem.label)).end }}
                          <i v-if="isLinkDisabled(subSubitem)" class="bx bx-lock-alt icon"></i>
                        </span>
                      </router-link>
                      <b-badge v-if="subSubitem.badge" :variant="subSubitem.badge.variant" pill>
                        {{ subSubitem.badge.text }}
                      </b-badge>
                    </li>
                  </ul>
                </b-collapse>
              </li>
            </ul>
          </b-collapse>
        </li>
      </template>
    </ul>
  </div>
  <!-- Sidebar -->
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { metaAuthorization } from '@common/router/meta-authorization';

export default {
  data() {
    return {
      menuItems: this.items,
      currentItem: null,
      menu: null,
      menuByLevel: { 0: [], 1: [], 2: [] },
    };
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    backRouteLink: {
      type: Object,
    },
    backRouteLabel: {
      type: String,
    },
    isContextualSideNav: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('currentUser', ['startOptions']),
    ...mapGetters('currentUser', {
      isBackOfficeUser: 'hasBackOfficeAccessRight',
      isDeployTemplateUser: 'hasDeployTemplateRight',
      currentSite: 'currentSite',
    }),
    ...mapState('layout', ['sidebarDisplayedState']),
    routeSiteId() {
      return this.$route.params.siteId ?? this.startOptions.default_site_id;
    },
    routeLocale() {
      return this.$route.params.locale ?? this.startOptions.default_language_id;
    },
  },
  watch: {
    currentSite: {
      handler: 'refreshMenu',
    },
  },
  mounted: function() {
    const init = (level, item) => {
      item.level = level;
      this.menuByLevel[level].push(item);

      if (item.subItems) {
        item.subItems.forEach(init.bind(this, level + 1));
      }
    };
    this.menuItems.forEach(init.bind(this, 0));
    this.refreshMenu();
  },
  methods: {
    filterItems(menuItems) {
      return menuItems.filter(item => {
        return !(item.siteId && !item.siteId.includes(this.$route.params.siteId));
      });
    },
    getActiveColor(item) {
      if (item.activeColor && item.active) {
        return `${item.activeColor()} mm-active`;
      }
      return '';
    },
    isLinkDisabled(item) {
      return (item.accessForbidden && !item.dontDisplayPadlockIfNoAccess) || item.link === '/todo';
    },
    handleComputedLabel(label) {
      if (typeof label === 'function') {
        return label();
      } else {
        return this.$t(label);
      }
    },
    getLink(item) {
      return (item.accessForbidden && item.dontDisplayPadlockIfNoAccess) || item.link === '/todo'
        ? {
            name: 'page-error',
            query: {
              askedPage: item.link.name,
              status_code: 403,
            },
          }
        : { ...item.link };
    },
    accessForbidden(item) {
      // beware: metaFn might not have been called at this point

      const resolvedRoute = this.$router.resolve(item.link);

      // Check if any matched route record has dynamic segments for siteId or locale
      const requiresSiteId = resolvedRoute.matched.some(record => record.path.includes(':siteId'));
      const requiresLocale = resolvedRoute.matched.some(record => record.path.includes(':locale'));

      // Build the params object conditionally
      const params = {};
      if (requiresSiteId) {
        params.siteId = this.routeSiteId;
      }
      if (requiresLocale) {
        params.locale = this.routeLocale;
      }

      return !metaAuthorization(this.$router.resolve({ ...item.link, params })?.meta || {});
    },
    subAccessForbidden(items) {
      let every = true;
      for (const item of items) {
        let result = false;
        if (item.link) {
          result = this.accessForbidden(item);
        }
        if (item.subItems) {
          result = this.subAccessForbidden(item.subItems);
        }
        every = every && result;
        item.accessForbidden = result;
      }
      return every;
    },
    isBackOfficeAccessForbidden(item) {
      return item.backOffice && !this.isBackOfficeUser;
    },
    isDeployTemplateAccessForbidden(item) {
      return item.deployTemplate && !this.isDeployTemplateUser;
    },
    isFullUrl(item) {
      return item.link && item.link.href;
    },
    restrictedMenuEntry: function(item) {
      return this.isBackOfficeAccessForbidden(item) || this.isDeployTemplateAccessForbidden(item);
    },
    splitLabel(text) {
      const splitted = text.split(' ');
      const end = splitted.pop();
      return {
        start: splitted.join(' '),
        end,
      };
    },
    /**
     * Returns true or false if given menu item has a child or not
     * @param item menuItem
     */
    hasItems(item) {
      return item.subItems !== undefined ? item.subItems.length > 0 : false;
    },
    /**
     * creates menu and highlights the matching tree if exists
     *  */
    refreshMenu() {
      this.subAccessForbidden(this.menuItems);
    },
    toggleOpened(item) {
      if (item.forcedOpened) {
        return;
      }
      if (!this.sidebarDisplayedState) {
        return;
      }
      if (!item.opened) {
        this.menuByLevel[item.level].forEach(i => (i.opened = false));
      }
      item.opened = !item.opened;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@versions/x/styles/variables.scss';

#sidebar-menu ul li a:hover {
  text-decoration: none;
  color: $sidebar-hover-text-color;

  i {
    color: $sidebar-hover-text-color;
  }
}

.go-back-link {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ebeff2;
  text-decoration: none !important;
  padding: 2px 0px 9px 16px;
  > i {
    font-size: x-large;
    margin-right: 5px;
  }
  > span {
    margin-top: 2px;
  }
  &:hover {
    color: $sidebar-hover-text-color;
  }
  &-minimized {
    @extend .go-back-link;
    padding: 15px 0px;
    justify-content: center;

    > i {
      margin-right: 0px;
    }
  }
}

.badge-blue {
  font-size: 12px;
  background-color: $commandersactblue !important;
  border-color: $commandersactblue !important;
}

.badge-red {
  font-size: 12px;
  background-color: $commandersact !important;
  border-color: $commandersact !important;
}
</style>
